import { useMemo, useState } from "react";
import { Box, Typography, Chip, Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useGetList } from "react-admin";
import { useAppTheme } from "@/hooks/useAppTheme";
import { ResourceName, type StoplossClaim } from "@/types";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";

/** Claim chip colour (matches the "claim" entry in CODE_CHIP_COLORS) */
const CLAIM_CHIP = { bg: "#f5f3ff", color: "#5b21b6" };

interface ClaimIdPickerProps {
  /** Policy ID to filter stoploss claims */
  policyId: string;
  /** Currently selected claim IDs */
  claimIds: string[];
  /** Whether editing is allowed (user has edit permission) */
  canEdit: boolean;
  /** Called with the full updated claim IDs array when user adds or removes */
  onUpdate: (claimIds: string[]) => void;
}

/**
 * Renders a labelled section of credited stoploss-claim chips with an
 * Autocomplete picker for adding claims. Claims are fetched for the
 * current policy so users can only select valid, existing claims.
 */
export const ClaimIdPicker = ({ policyId, claimIds, canEdit, onUpdate }: ClaimIdPickerProps) => {
  const { theme } = useAppTheme();
  const [isEditing, setIsEditing] = useState(false);

  // Fetch stoploss claims for this policy — needed both for chip labels and the picker
  const { data: claims = [], isLoading } = useGetList<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "aegle_id", order: "ASC" },
      filter: { policy_id_ref: policyId },
    },
    { enabled: isEditing || claimIds.length > 0 }
  );

  // Lookup map for chip labels
  const claimLookup = useMemo(() => new Map(claims.map((c) => [c.id, c])), [claims]);

  // Filter out claims that are already selected
  const availableClaims = claims.filter((c) => !claimIds.includes(c.id));

  const handleAdd = (claim: StoplossClaim | null) => {
    if (!claim || claimIds.includes(claim.id)) return;
    onUpdate([...claimIds, claim.id]);
  };

  const handleRemove = (id: string) => {
    onUpdate(claimIds.filter((c) => c !== id));
  };

  /** Render a claim chip — shows aegle_id if available, otherwise truncated UUID */
  const chipLabel = (id: string) => {
    const claim = claimLookup.get(id);
    return claim ? claim.aegle_id : `${id.slice(0, 8)}…`;
  };

  return (
    <>
      {/* Section header with Edit/Done toggle */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, mb: 0.5 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled" }}>
          Credited Claims
        </Typography>
        {canEdit && (
          <Typography
            variant="caption"
            onClick={() => setIsEditing(!isEditing)}
            sx={{
              cursor: "pointer",
              color: "primary.main",
              fontWeight: 500,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {isEditing ? "Done" : "Edit"}
          </Typography>
        )}
      </Box>

      {/* Chip container */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          p: 1.5,
          minHeight: 36,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
          {claimIds.map((id) => (
            <Chip
              key={id}
              label={chipLabel(id)}
              size="small"
              onDelete={isEditing ? () => handleRemove(id) : undefined}
              sx={{
                bgcolor: CLAIM_CHIP.bg,
                color: CLAIM_CHIP.color,
                fontFamily: "monospace",
                fontSize: "0.75rem",
                fontWeight: 500,
                "& .MuiChip-deleteIcon": { color: CLAIM_CHIP.color, fontSize: 14 },
              }}
            />
          ))}

          {/* Autocomplete picker — visible only in edit mode */}
          {isEditing && (
            <Autocomplete
              size="small"
              options={availableClaims}
              loading={isLoading}
              getOptionLabel={(option) => option.aegle_id}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              onChange={(_e, newValue) => handleAdd(newValue)}
              value={null} // always reset after selection
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                      {option.aegle_id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatToTitleCase(option.claim_status)}
                      {option.total_paid_amount !== null &&
                        option.total_paid_amount !== undefined &&
                        ` · $${option.total_paid_amount.toLocaleString()}`}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select claim…"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
              sx={{
                minWidth: 180,
                maxWidth: 240,
                "& .MuiInputBase-root": { height: 32, fontSize: "0.75rem" },
                "& .MuiInputBase-input": { py: 0.5, px: 0.75 },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: CLAIM_CHIP.color },
              }}
            />
          )}

          {claimIds.length === 0 && !isEditing && (
            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
              None
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};
